import React, { useRef, useEffect } from 'react';

// Minimal typing for the hls.js global loaded from CDN — loading the player
// script at runtime keeps hls.js out of the bundle.
declare global {
  interface Window {
    Hls?: {
      new (config?: Record<string, unknown>): {
        loadSource(url: string): void;
        attachMedia(el: HTMLVideoElement): void;
        on(event: string, cb: (event: string, data: { fatal?: boolean; type?: string }) => void): void;
        startLoad(): void;
        recoverMediaError(): void;
        destroy(): void;
      };
      isSupported(): boolean;
      Events: { MANIFEST_PARSED: string; ERROR: string };
      ErrorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string };
    };
  }
}

const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.20/dist/hls.min.js';

let hlsScriptPromise: Promise<void> | null = null;
const loadHlsScript = (): Promise<void> => {
  if (window.Hls) return Promise.resolve();
  if (hlsScriptPromise) return hlsScriptPromise;
  hlsScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = HLS_CDN;
    script.onload = () => resolve();
    script.onerror = () => {
      hlsScriptPromise = null;
      reject(new Error('Failed to load hls.js'));
    };
    document.head.appendChild(script);
  });
  return hlsScriptPromise;
};

interface HlsPlayerProps {
  videoUrl: string;
  title?: string;
  className?: string;
  width?: string | number;
  height?: string | number;
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
  loop?: boolean;
  poster?: string;
  onLoadedData?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: unknown) => void;
}

export const HlsPlayer: React.FC<HlsPlayerProps> = ({
  videoUrl,
  title,
  className = '',
  width = '100%',
  height = '100%',
  autoplay = false,
  muted = false,
  controls = true,
  loop = false,
  poster,
  onLoadedData,
  onPlay,
  onPause,
  onEnded,
  onError,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<InstanceType<NonNullable<Window['Hls']>> | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let destroyed = false;

    // Autoplay with sound when the browser allows it; otherwise fall back to a
    // muted autoplay (always permitted). Skip players that aren't rendered
    // (e.g. the display:none mobile/desktop twin) so two copies never play.
    const tryAutoplay = () => {
      if (!autoplay || destroyed || video.getClientRects().length === 0) return;
      video.play().catch((e: { name?: string }) => {
        if (destroyed || e?.name !== 'NotAllowedError') return;
        video.muted = true;
        video.play().catch(() => undefined);
      });
    };

    const playNatively = () => {
      video.src = videoUrl;
      tryAutoplay();
    };

    const start = async () => {
      // Prefer hls.js (MSE) wherever it works. Native HLS is only the fallback
      // for iOS Safari: recent Chrome answers canPlayType(m3u8) with "maybe" but
      // then fails this stream with "no supported source", leaving a dead player.
      try {
        await loadHlsScript();
      } catch (e) {
        if (video.canPlayType('application/vnd.apple.mpegurl')) playNatively();
        else onError?.(e);
        return;
      }
      if (destroyed || !window.Hls) return;
      if (!window.Hls.isSupported()) {
        if (video.canPlayType('application/vnd.apple.mpegurl')) playNatively();
        else onError?.(new Error('HLS not supported in this browser'));
        return;
      }
      const hls = new window.Hls({ enableWorker: true, lowLatencyMode: false, backBufferLength: 90 });
      hlsRef.current = hls;
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      hls.on(window.Hls.Events.MANIFEST_PARSED, tryAutoplay);
      hls.on(window.Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || !window.Hls) return;
        if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else {
          hls.destroy();
          onError?.(data);
        }
      });
    };
    start();

    return () => {
      destroyed = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  return (
    <div
      className={`hls-player-container ${className}`}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    >
      <video
        ref={videoRef}
        title={title}
        muted={muted}
        controls={controls}
        loop={loop}
        poster={poster}
        playsInline
        preload="metadata"
        onLoadedData={onLoadedData}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
      />
    </div>
  );
};

export default HlsPlayer;
